/** "Is your ISP answering your DNS?" — device visual + two cards. The device
 *  shows a green check when none of the observed resolvers match a known ISP
 *  resolver, a yellow exclamation when one does. Device glyph from the mockup. */
import type { Phase, ResolverRow } from "../lib/types";

interface Props {
  servers: ResolverRow[];
  phase: Phase;
  reducedMotion: boolean;
}

function DeviceBadge({ state, reducedMotion }: { state: "idle" | "ok" | "isp"; reducedMotion: boolean }) {
  const color = state === "ok" ? "#2BE82B" : state === "isp" ? "#DCF300" : "#5C5F5A";
  const label = state === "ok" ? "NO ISP DNS" : state === "isp" ? "ISP DNS" : "UNTESTED";
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 7 }}>
      <svg width={34} height={34} viewBox="0 0 24 24" style={state === "ok" && !reducedMotion ? { filter: "drop-shadow(0 0 6px rgba(43,232,43,0.7))" } : undefined}>
        <path d="M12 2 4 5v6c0 5 3.4 8.5 8 10 4.6-1.5 8-5 8-10V5l-8-3Z" fill="none" stroke={color} strokeWidth={1.8} strokeLinejoin="round" />
        {state === "isp" ? (
          <g>
            <line x1={12} y1={8.3} x2={12} y2={13} stroke={color} strokeWidth={1.8} strokeLinecap="round" />
            <circle cx={12} cy={15.7} r={1.05} fill={color} />
          </g>
        ) : (
          <path d="m8.4 12 2.3 2.3 4.6-4.6" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
        )}
      </svg>
      <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9.5, fontWeight: 700, letterSpacing: "0.12em", color }}>{label}</span>
    </div>
  );
}

function short(r: ResolverRow): string {
  if (r.publicName) return r.publicName;
  if (r.org) return r.org.split(" - ")[0]?.trim() || r.org;
  return r.ip;
}

export function LeakDiagram({ servers, phase, reducedMotion }: Props) {
  const done = phase === "done";
  const isp = servers.filter((r) => r.knownIsp);
  const state: "idle" | "ok" | "isp" = !done || servers.length === 0 ? "idle" : isp.length ? "isp" : "ok";
  const badge = <DeviceBadge state={state} reducedMotion={reducedMotion} />;
  const ispNames = [...new Set(isp.map((r) => r.knownIsp!))].join(", ");
  const names = [...new Set(servers.map(short))].join(", ");

  const resultColor = state === "isp" ? "#DCF300" : state === "ok" ? "#2BE82B" : "#5C5F5A";
  const resultBorder = state === "isp" ? "rgba(220,243,0,0.45)" : state === "ok" ? "rgba(43,232,43,0.5)" : "#2A2D29";
  const resultTitle = state === "isp" ? "ISP DNS DETECTED" : state === "ok" ? "NO ISP DNS DETECTED" : "RUN THE TEST";
  const resultBody =
    state === "isp"
      ? `${ispNames} — a known ISP resolver — answered your lookups. Your ISP can see every domain you visit, tied to your account.`
      : state === "ok"
        ? `None of the DNS servers that answered (${names}) match a known ISP resolver.`
        : "We compare every DNS server that answers your lookups against a database of known ISP resolvers.";
  const adviceBody =
    state === "isp"
      ? "Switch to an encrypted public resolver such as Quad9 or Cloudflare DNS, or let your VPN handle DNS with its leak protection turned on. Then run again."
      : state === "ok"
        ? "Keep it that way: if you use a VPN, leave its DNS protection on; otherwise an encrypted public resolver keeps your ISP out of your lookups."
        : "If a match is found, the device above shows a warning and we'll tell you which ISP it is and what to change.";

  return (
    <div style={{ width: "100%", padding: "48px 0 6px 0", display: "flex", flexDirection: "column", alignItems: "center", gap: 22 }}>
      <div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: 6 }}>
        <span style={{ fontFamily: "'Sora', sans-serif", fontSize: 18, fontWeight: 700, color: "#DEDFDF" }}>Is your ISP answering your DNS?</span>
        <span style={{ fontSize: 13, color: "#8A8D88" }}>Your DNS servers, cross-matched against a database of known ISP resolvers.</span>
      </div>
      <div data-rt-diagram-grid="" style={{ width: "100%", display: "grid", gridTemplateColumns: "140px 1fr", gap: "20px 24px", alignItems: "center" }}>
        <div data-rt-device="" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
          <div data-dev-desktop="" style={{ flexDirection: "column", alignItems: "center", flex: "none" }}>
            <div style={{ width: 136, height: 90, boxSizing: "border-box", background: "#0A0B0A", border: "1px solid #2A2D29", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>{badge}</div>
            <div style={{ width: 30, height: 14, background: "#141614", borderLeft: "1px solid #2A2D29", borderRight: "1px solid #2A2D29" }} />
            <div style={{ width: 78, height: 7, borderRadius: 999, background: "#141614", border: "1px solid #2A2D29" }} />
          </div>
          <div data-dev-phone="" style={{ width: 86, height: 150, boxSizing: "border-box", background: "#0A0B0A", border: "1px solid #2A2D29", borderRadius: 20, flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 12, flex: "none" }}>
            {badge}
          </div>
          <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "#5C5F5A" }}>your device</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0 }}>
          <div style={{ background: "#0A0B0A", border: `1.5px solid ${resultBorder}`, borderRadius: 16, padding: "16px 18px", display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: resultColor, boxShadow: state === "ok" ? "0 0 8px rgba(43,232,43,0.8)" : "none", flex: "none" }} />
              <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.12em", color: resultColor, background: state === "idle" ? "transparent" : `${resultColor}14`, border: `1px solid ${resultBorder}`, borderRadius: 999, padding: "3px 9px" }}>{resultTitle}</span>
            </div>
            <span style={{ fontSize: 12.5, color: "#8A8D88", lineHeight: 1.6 }}>{resultBody}</span>
          </div>
          <div style={{ background: "#0A0B0A", border: "1.5px dashed #2A2D29", borderRadius: 16, padding: "16px 18px", display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.12em", color: "#B9BCB6" }}>WHAT TO DO</span>
            <span style={{ fontSize: 12.5, color: "#8A8D88", lineHeight: 1.6 }}>{adviceBody}</span>
          </div>
          <span style={{ fontSize: 11, color: "#5C5F5A" }}>ISP resolver database: <a href="https://dnschecker.org/public-dns/" target="_blank" rel="noopener noreferrer" style={{ color: "#8A8D88" }}>dnschecker.org</a>.</span>
        </div>
      </div>
    </div>
  );
}
